// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/** `register_surface` values the pack's templates declare to feed these registers. */
export const PRODUCT_IDENTIFICATION_REGISTER_SURFACES = {
    /** A person's scope and risk-tier determination for one product line. */
    scopeDetermination: 'product_identification_scope_determination',
    /** The identifier as returned and the master data of a product or batch. */
    uidRegister: 'product_identification_uid_register',
    /** A significant supply-chain event with its certificate evidence and any supplier correction. */
    eventRecord: 'product_identification_event_record',
    /** A certificate synchronisation receipt, recorded by pointer only. */
    certificateReceipt: 'product_identification_certificate_receipt',
    /** A service provider's data-responsibility agreement. */
    providerAgreement: 'product_identification_provider_agreement',
    /** A system's connection route and transition readiness. */
    connectionReadiness: 'product_identification_connection_readiness',
};
/**
 * The participation class a scope-determination option declares. Every count,
 * filter and duty states one of these, and a compulsory class is never merged
 * with a voluntary one.
 */
export const SCOPE_PARTICIPATION_CLASSES = [
    'compulsory',
    'voluntary',
    'outside_scope',
    'compulsory_rules_disapplied',
    'not_determined',
];
/**
 * The attribute names a product-identification register reads off installed
 * options and requirements. The pack declares the values; these are only the
 * keys. An attribute the pack did not author reads as absent (false).
 *
 * On an OPTION of a template declaring the scope-determination surface:
 * - `participation` — the option's {@link ScopeParticipationClass};
 * - `requires_instrument` — choosing it requires the instrument relied on;
 * - `raises_tier_conditioned_duties` — choosing it raises the framework's
 *   requirements that carry `tier_conditioned: true` (only on a compulsory
 *   option);
 * - `cites_framework_code` — an instrument option offered as a cited basis:
 *   the framework code must appear in the `existing_basis` of a tracked
 *   follow-on item of the template's framework. Never preselected.
 *
 * - `raises_tier_conditioned_duties_voluntarily` — on an option of the
 *   voluntary-participation decision (never on a determination option): when a
 *   line whose current determination is `voluntary` records this option, the
 *   tier-conditioned requirements are shown as applying to how it takes part,
 *   under the voluntary label, and never counted as mandatory (pack 1.0.17,
 *   legal reading P2-1, owner answer 2026-11-03).
 *
 * On a REQUIREMENT:
 * - `tier_conditioned` — a duty raised for a product line only when its current
 *   determination option carries `raises_tier_conditioned_duties`; a duty without
 *   it carries the condition its text states and is never gated on a product
 *   line's determination.
 */
export const PRODUCT_IDENTIFICATION_PACK_ATTR = {
    participation: 'participation',
    requiresInstrument: 'requires_instrument',
    raisesTierConditionedDuties: 'raises_tier_conditioned_duties',
    citesFrameworkCode: 'cites_framework_code',
    tierConditioned: 'tier_conditioned',
    raisesTierConditionedDutiesVoluntarily: 'raises_tier_conditioned_duties_voluntarily',
};
// ── Sprint 62.47.0 Slice A — scope determinations and the sector watch ──
/** One row per determination VERSION; exactly one `current` per (org, product, framework). */
export const PRODUCT_IDENTIFICATION_SCOPE_DETERMINATIONS_COLLECTION = 'grc_product_identification_scope_determinations';
/** Every collection the product-identification module writes. */
export const PRODUCT_IDENTIFICATION_COLLECTIONS = {
    scopeDeterminations: PRODUCT_IDENTIFICATION_SCOPE_DETERMINATIONS_COLLECTION,
};
/** Error codes the product-identification routes answer with. */
export const PRODUCT_IDENTIFICATION_ERROR_CODES = {
    SURFACE_NOT_DECLARED: 'pi_scope_surface_not_declared',
    UNKNOWN_FRAMEWORK: 'pi_scope_unknown_framework',
    UNKNOWN_OPTION: 'pi_scope_unknown_option',
    REGIME_FIELD_NOT_DECLARED: 'pi_scope_regime_field_not_declared',
    FIELD_REQUIRED: 'pi_scope_field_required',
    INSTRUMENT_REQUIRED: 'pi_scope_instrument_required',
    UNKNOWN_USER: 'pi_scope_unknown_user',
    SELF_REVIEW: 'pi_scope_self_review_refused',
    UNKNOWN_PRODUCT: 'pi_scope_unknown_product',
    VERSION_CONFLICT: 'pi_scope_version_conflict',
};
//# sourceMappingURL=product-identification.js.map